import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { Settings, ArrowRight, Gift, Calendar, DollarSign } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { getAccessibleHomeIds, getAccessibleHomes } from '../../lib/homeMembership';
import { useNavigate } from 'react-router-dom';
import { shadows } from '../../lib/styles';

interface Home {
  id: string;
  address: string;
}

interface HomeSystem {
  home_id: string;
  heating_systems: string[];
  cooling_systems: string[];
  appliances: string[];
  other_systems: string[];
  upgrades: string[];
  outdoor_features: string[];
}

const ImportantReminders: React.FC = () => {
  const navigate = useNavigate();
  const [unconfiguredHomes, setUnconfiguredHomes] = useState<Home[]>([]);
  const [invitationsRemaining, setInvitationsRemaining] = useState<number>(0);
  const [hasWalkthrough, setHasWalkthrough] = useState<boolean>(false);
  const [unpaidExpensesTotal, setUnpaidExpensesTotal] = useState<number>(0);
  const [unpaidExpensesCount, setUnpaidExpensesCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkHomeSystems();
    checkReferralInvitations();
    checkWalkthrough();
    checkUnpaidExpenses();
  }, []);

  const checkReferralInvitations = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return;
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('referral_invitations_remaining')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      if (profile && profile.referral_invitations_remaining > 0) {
        setInvitationsRemaining(profile.referral_invitations_remaining);
      }
    } catch (error) {
      console.error('Error checking referral invitations:', error);
    }
  };

  const checkWalkthrough = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return;
      }

      // Get user's homes
      const homeIds = await getAccessibleHomeIds(user.id);
      if (homeIds.length === 0) {
        return;
      }

      // Check if user has any scheduled or completed walkthrough visits
      const { data: walkthroughs, error: walkthroughError } = await supabase
        .from('visits')
        .select('id')
        .in('home_id', homeIds)
        .eq('visit_type', 'walkthrough')
        .in('status', ['scheduled', 'completed']);

      if (walkthroughError) throw walkthroughError;

      setHasWalkthrough((walkthroughs?.length || 0) > 0);
    } catch (error) {
      console.error('Error checking walkthrough:', error);
    }
  };

  const checkUnpaidExpenses = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return;
      }

      // Get user's homes
      const homeIds = await getAccessibleHomeIds(user.id);
      if (homeIds.length === 0) {
        return;
      }

      const { data: standaloneExpenses, error: standaloneError } = await supabase
        .from('service_request_expenses')
        .select('id, amount')
        .in('home_id', homeIds)
        .is('service_request_id', null)
        .is('parent_expense_id', null)
        .eq('billable_to_homeowner', true)
        .eq('is_paid', false);

      if (standaloneError) throw standaloneError;

      const { data: serviceRequests, error: requestsError } = await supabase
        .from('service_requests')
        .select('id')
        .in('home_id', homeIds);

      if (requestsError) throw requestsError;

      let linkedExpenses: { id: string; amount: number | string }[] = [];
      const requestIds = (serviceRequests || []).map((request) => request.id);

      if (requestIds.length > 0) {
        const { data: serviceRequestExpenses, error: expensesError } = await supabase
          .from('service_request_expenses')
          .select('id, amount')
          .in('service_request_id', requestIds)
          .eq('billable_to_homeowner', true)
          .eq('is_paid', false)
          .is('parent_expense_id', null);

        if (expensesError) throw expensesError;
        linkedExpenses = serviceRequestExpenses || [];
      }

      const unpaidExpenses = [...(standaloneExpenses || []), ...linkedExpenses];

      if (unpaidExpenses.length > 0) {
        const total = unpaidExpenses.reduce(
          (sum, expense) => sum + parseFloat(expense.amount.toString()),
          0
        );
        setUnpaidExpensesTotal(total);
        setUnpaidExpensesCount(unpaidExpenses.length);
      }
    } catch (error) {
      console.error('Error checking unpaid expenses:', error);
    }
  };

  const checkHomeSystems = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      const homes = await getAccessibleHomes(user.id);
      if (!homes || homes.length === 0) {
        setLoading(false);
        return;
      }

      const { data: systems, error: systemsError } = await supabase
        .from('home_systems')
        .select('home_id, heating_systems, cooling_systems, appliances, other_systems, upgrades, outdoor_features')
        .in('home_id', homes.map(h => h.id));

      if (systemsError) throw systemsError;

      const unconfigured = homes.filter(home => {
        const homeSystem = systems?.find(s => s.home_id === home.id);
        if (!homeSystem) return true;

        const hasConfiguration = Object.values(homeSystem).some(value => 
          Array.isArray(value) && value.length > 0
        );
        return !hasConfiguration;
      });

      setUnconfiguredHomes(unconfigured);
    } catch (error) {
      console.error('Error checking home systems:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color="#3b82f6" />
      </View>
    );
  }

  if (unconfiguredHomes.length === 0 && invitationsRemaining === 0 && hasWalkthrough && unpaidExpensesCount === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <View style={styles.list}>
          {!hasWalkthrough && (
            <View style={styles.reminderItem}>
              <View style={[styles.reminderIcon, styles.calendarIcon]}>
                <Calendar size={16} color="#2563eb" />
              </View>
              <View style={styles.reminderContent}>
                <View style={styles.reminderHeader}>
                  <View style={styles.reminderTextContainer}>
                    <Text style={styles.reminderLabel}>Schedule Your Walkthrough</Text>
                    <Text style={styles.reminderDescription}>
                      Get started with a personalized walkthrough of your home. Schedule a time that works for you.
                    </Text>
                  </View>
                  <Pressable
                    onPress={() => navigate('/schedule-walkthrough')}
                    style={styles.configureButton}
                  >
                    <Text style={styles.configureText}>Schedule</Text>
                    <ArrowRight size={16} color="#2563eb" />
                  </Pressable>
                </View>
              </View>
            </View>
          )}
          {invitationsRemaining > 0 && (
            <View style={styles.reminderItem}>
              <View style={[styles.reminderIcon, styles.giftIcon]}>
                <Gift size={16} color="#7c3aed" />
              </View>
              <View style={styles.reminderContent}>
                <View style={styles.reminderHeader}>
                  <View style={styles.reminderTextContainer}>
                    <Text style={styles.reminderLabel}>Share HoneyDone with Friends</Text>
                    <Text style={styles.reminderDescription}>
                      You can invite {invitationsRemaining} more {invitationsRemaining === 1 ? 'person' : 'people'}. Share your link and earn credits!
                    </Text>
                  </View>
                  <Pressable
                    onPress={() => navigate('/referrals')}
                    style={styles.configureButton}
                  >
                    <Text style={styles.shareText}>Share</Text>
                    <ArrowRight size={16} color="#7c3aed" />
                  </Pressable>
                </View>
              </View>
            </View>
          )}
          {unpaidExpensesCount > 0 && (
            <View style={styles.reminderItem}>
              <View style={[styles.reminderIcon, styles.expenseIcon]}>
                <DollarSign size={16} color="#dc2626" />
              </View>
              <View style={styles.reminderContent}>
                <View style={styles.reminderHeader}>
                  <View style={styles.reminderTextContainer}>
                    <Text style={styles.reminderLabel}>Unpaid Expenses</Text>
                    <Text style={styles.reminderDescription}>
                      You have {unpaidExpensesCount} unpaid {unpaidExpensesCount === 1 ? 'expense' : 'expenses'} totaling {formatCurrency(unpaidExpensesTotal)}.
                    </Text>
                  </View>
                  <Pressable
                    onPress={() => navigate('/expenses')}
                    style={styles.configureButton}
                  >
                    <Text style={styles.expenseText}>View</Text>
                    <ArrowRight size={16} color="#dc2626" />
                  </Pressable>
                </View>
              </View>
            </View>
          )}
          {unconfiguredHomes.length > 0 && (
            <View style={styles.reminderItem}>
              <View style={styles.reminderIcon}>
                <Settings size={16} color="#2563eb" />
              </View>
              <View style={styles.reminderContent}>
                <View style={styles.reminderHeader}>
                  <View style={styles.reminderTextContainer}>
                    <Text style={styles.reminderLabel}>Configure Home Systems</Text>
                    <Text style={styles.reminderDescription}>
                      {unconfiguredHomes.length === 1 
                        ? `Set up systems for ${unconfiguredHomes[0].address}`
                        : `${unconfiguredHomes.length} homes need system configuration`
                      }
                    </Text>
                  </View>
                  <Pressable
                    onPress={() => navigate('/maintenance-plan')}
                    style={styles.configureButton}
                  >
                    <Text style={styles.configureText}>Configure</Text>
                    <ArrowRight size={16} color="#2563eb" />
                  </Pressable>
                </View>
              </View>
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    padding: 16,
    alignItems: 'center',
  },
  container: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  content: {
    padding: 16,
  },
  list: {
    gap: 16,
  },
  reminderItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  reminderIcon: {
    width: 28,
    height: 28,
    backgroundColor: '#eff6ff',
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2,
  },
  giftIcon: {
    backgroundColor: '#f5f3ff',
  },
  calendarIcon: {
    backgroundColor: '#eff6ff',
  },
  expenseIcon: {
    backgroundColor: '#fee2e2',
  },
  reminderContent: {
    flex: 1,
  },
  reminderHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  reminderTextContainer: {
    flex: 1,
  },
  reminderLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  reminderDescription: {
    fontSize: 14,
    color: '#4b5563',
    marginTop: 2,
  },
  configureButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  configureText: {
    fontSize: 14,
    color: '#2563eb',
    ...(Platform.OS === 'web' && {
      textDecorationLine: 'underline',
    }),
  },
  shareText: {
    fontSize: 14,
    color: '#7c3aed',
    ...(Platform.OS === 'web' && {
      textDecorationLine: 'underline',
    }),
  },
  expenseText: {
    fontSize: 14,
    color: '#dc2626',
    ...(Platform.OS === 'web' && {
      textDecorationLine: 'underline',
    }),
  },
});

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

export default ImportantReminders;
